import React from 'react';

import { Controller } from 'react-hook-form';
import Cookies from 'js-cookie';
import clsx from 'clsx';

import { setData } from 'prequalificationV2/actions/data';
import { setIsBorrowerManualFlow, setSessionId } from 'prequalificationV2/actions/application';
import { setProveErrorInfo } from 'prequalificationV2/actions/proveErrorInfo';
import FormHeader from 'prequalificationV2/components/FormHeader';
import Loader from 'components/Loader';
import FormBody from 'components/FormBody';
import { Button } from 'components/Buttons/Button';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import {
  LoadingType,
  PreQualificationStep,
  setAccount,
  setLoadingState,
  setPreQualificationStep,
  setSignInStep,
  SignInStep,
} from 'prequalificationV2/actions';
import { LoanInformationVariables } from 'prequalificationV2/enums/LoanInformationVariables';
import { removeNonDigits, removeSpaces } from 'prequalificationV2/utils/stringUtils';
import { CheckedImage } from 'assets/icons';
import PhoneNumberInput from 'components/Inputs/PhoneNumberInput';
import NumberInput from 'components/Inputs/NumberInput';
import Input from 'components/Inputs/Input';
import { Checkbox, CheckboxType } from 'components/Checkbox';
import { setBorrower } from 'prequalificationV2/actions/borrower';
import { AppRoutes } from 'prequalificationV2/routes/routes.enums';
import { BorrowerIdentityFormType } from 'prequalificationV2/pages/PreQualification/steps/BorrowerIdentity/borrowerIdentity.types';
import { IS_PROVE_PREFILL_ENABLED } from 'constants/constants';
import { ProveAuthType } from 'enums/ProveAuthType';
import { preQualificationApi } from 'prequalificationV2/api/PreQualification/PreQualificationApi';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import useAppNavigate from 'hooks/useAppNavigate';
import { proveApi } from 'api/ProveApi';
import * as notificationManager from 'notifications';
import { FailureNotificationType } from 'notifications';
import { LOAN_OPTIONS_NAMES } from 'prequalificationV2/components/Offers/offers.constants';
import { CONCEPTUAL_DEVICE_ID_COOKIE } from 'prequalificationV2/routes/routes.constants';

import styles from './BorrowerIdentity.module.scss';
import { useBorrowerIdentityForm } from './hooks/useBorrowerIdentityForm';

const BorrowerIdentity = () => {
  const isGlobalLoading = useAppSelector((state) => state.loading[LoadingType.GetData]);
  const { isBorrowerAuthorized } = useAppSelector((state) => state.prequalificationPortal);

  const dispatch = useAppDispatch();
  const navigator = useAppNavigate();
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search.slice(1));
  const { contractorAutofillUsed } = useAppSelector((state) => state.data);

  const saveInformationToStore = (values: BorrowerIdentityFormType) => {
    dispatch(
      setBorrower({
        email: values[LoanInformationVariables.BorrowerEmail],
        phone: values[LoanInformationVariables.BorrowerCellPhone],
        dateOfBirth: values[LoanInformationVariables.BorrowerDateOfBirth],
        fullConsentAgreed: !!values[LoanInformationVariables.FullConsentCheckbox],
        consentAgreed: true,
      }),
    );

    dispatch(
      setData({
        isIframe: !!params.getAll('isIframe')[0],
        device: Cookies.get('device'),
        network: Cookies.get('network'),
        refer: Cookies.get('refer'),
        sourcename: Cookies.get('sourcename'),
        sourcetype: Cookies.get('sourcetype'),
        campaignId: Cookies.get('campaignId'),
        adGroupId: Cookies.get('adGroupId'),
        utmSource: Cookies.get('_utm_source') || Cookies.get('pys_utm_source'),
        utmMedium: Cookies.get('_utm_medium') || Cookies.get('pys_utm_medium'),
        utmCampaign: Cookies.get('_utm_campaign') || Cookies.get('pys_utm_campaign'),
        utmTerm: Cookies.get('_utm_term') || Cookies.get('pys_utm_term'),
        utmContent: Cookies.get('_utm_content') || Cookies.get('pys_utm_content'),
        gclid: Cookies.get('_gcl_aw'),
        fbclid: Cookies.get('_fbc'),
        liFatId: Cookies.get('li_fat_id'),
        pintrestEpik: Cookies.get('_epik'),
        uetMsclkId: Cookies.get('_uetmsclkid'),
        jobTreadContractorUuid: Cookies.get('contractor_uuid') ?? null,
        jobTreadDocumentId: Cookies.get('document_id') ?? null,
        jobTreadLoanAmount: Cookies.get('loan_amount') ?? null,
        jobTreadContractorName: Cookies.get('contractor_name') ?? null,
      }),
    );
  };

  const invokeProve = async (values: BorrowerIdentityFormType) => {
    try {
      const authResponse = await proveApi.startInstantAuthentication({
        mobileNumber: removeNonDigits(values[LoanInformationVariables.BorrowerCellPhone]),
      });

      if (!!authResponse?.errorStatus) {
        dispatch(
          setProveErrorInfo({
            status: authResponse?.errorStatus,
            description: authResponse?.errorDescription,
          }),
        );
      }

      if (authResponse?.responseType === ProveAuthType.Mobile) {
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: true }));
        window.location.href = authResponse?.authenticationLink + '&r=t';

        return;
      }

      if (!!authResponse?.sessionId && authResponse?.responseType === ProveAuthType.Instant) {
        dispatch(
          setBorrower({
            firstName: '',
            lastName: '',
            streetNumber: '',
            streetName: '',
            city: '',
            state: '',
            zip: '',
            county: '',
            personalIdNumber: '',
            possessionCheck: {},
          }),
        );
        dispatch(setSessionId(authResponse?.sessionId));
        dispatch(setPreQualificationStep(PreQualificationStep.BorrowerPhoneVerification));
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
      } else {
        dispatch(setIsBorrowerManualFlow(true));
        dispatch(setPreQualificationStep(PreQualificationStep.LoanInformation));
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
      }
    } catch (err) {
      dispatch(setIsBorrowerManualFlow(true));
      dispatch(setPreQualificationStep(PreQualificationStep.LoanInformation));
      dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
    }
  };

  const submitForm = async (values: BorrowerIdentityFormType) => {
    try {
      dispatch(setLoadingState({ type: LoadingType.GetData, loading: true }));

      saveInformationToStore(values);

      let isMarketingConsentAllowedBeforeOtp = true;

      if (!isBorrowerAuthorized) {
        const borrower = await preQualificationApi.getBorrowerShortInfoByEmail({
          email: values[LoanInformationVariables.BorrowerEmail],
        });

        // Intent record skips OTP
        if (borrower?.id && !borrower.isIntentRecord) {
          isMarketingConsentAllowedBeforeOtp = false;

          dispatch(setAccount({ id: borrower.id, email: values[LoanInformationVariables.BorrowerEmail] }));
          dispatch(setSignInStep(SignInStep.PrequalOtpVerification));
          navigator(AppRoutes.Login);
        } else if (!borrower?.id) {
          await preQualificationApi.createBorrowerIntent({
            email: values[LoanInformationVariables.BorrowerEmail],
            phone: values[LoanInformationVariables.BorrowerCellPhone],
            dateOfBirth: values[LoanInformationVariables.BorrowerDateOfBirth],
            fullConsentAgreed: !!values[LoanInformationVariables.FullConsentCheckbox],
            deviceId: Cookies.get(CONCEPTUAL_DEVICE_ID_COOKIE),
            contractorAutofillUsed: contractorAutofillUsed,
          });

          if (IS_PROVE_PREFILL_ENABLED) {
            await invokeProve(values);
          }
        }
      }

      dispatch(setIsBorrowerManualFlow(true));
      dispatch(setPreQualificationStep(PreQualificationStep.BorrowerInfo));

      if (isMarketingConsentAllowedBeforeOtp) {
        await preQualificationApi.updateBorrowerMarketingConsent({
          email: values[LoanInformationVariables.BorrowerEmail],
          fullConsentAgreed: !!values[LoanInformationVariables.FullConsentCheckbox],
          consentAgreed: true,
        });
      }
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
    }
  };

  const { errors, setValue, trigger, watcher, clearErrors, handleSubmitLoanForm, isLoading, isSubmitted, control } =
    useBorrowerIdentityForm(submitForm);

  const handleCopyPaste = (event: React.ClipboardEvent) => event.preventDefault();

  return (
    <>
      {isGlobalLoading ? (
        <Loader />
      ) : (
        <>
          <div className={styles.infoContainer}>
            <div className={styles.infoContent}>Inquiry Does NOT Affect Your Credit Scores</div>
          </div>
          <div className={styles.infoContainer}>
            <div className={clsx(styles.infoContent, styles.steps)}>You’re just 4 easy steps away</div>
          </div>
          <div className={styles.options}>
            {LOAN_OPTIONS_NAMES.map((option) => (
              <div key={option} className={styles.option}>
                <CheckedImage />
                <p>{option}</p>
              </div>
            ))}
          </div>
          <FormHeader title="Contacts" percent={0} step={1} totalSteps={4} />
          <FormBody formBodyClassName={styles.formBody}>
            <div className={styles.blockTitle}>
              HFS will use information below to verify your identity and communicate with you.
            </div>
            <div className={styles.rowContainer}>
              <Input
                label={LoanInformationVariables.BorrowerEmail}
                errorMessage={errors[LoanInformationVariables.BorrowerEmail]?.message}
                className={styles.formInput}
                name={LoanInformationVariables.BorrowerEmail}
                onChange={(event: any) => {
                  clearErrors([LoanInformationVariables.BorrowerEmail]);
                  setValue(LoanInformationVariables.BorrowerEmail, removeSpaces(event.target.value?.toLowerCase()));

                  if (isSubmitted) {
                    trigger(LoanInformationVariables.BorrowerEmail);
                  }
                }}
                onCopy={handleCopyPaste}
                value={watcher[LoanInformationVariables.BorrowerEmail]}
                disabled={isBorrowerAuthorized}
              />
              <PhoneNumberInput
                label={LoanInformationVariables.BorrowerCellPhone}
                errorMessage={errors[LoanInformationVariables.BorrowerCellPhone]?.message}
                className={styles.formInput}
                name={LoanInformationVariables.BorrowerCellPhone}
                onChange={(value: string) => {
                  clearErrors([LoanInformationVariables.BorrowerCellPhone]);

                  setValue(LoanInformationVariables.BorrowerCellPhone, value);

                  if (isSubmitted) {
                    trigger(LoanInformationVariables.BorrowerCellPhone);
                  }
                }}
                onBlur={() => {
                  if (watcher[LoanInformationVariables.BorrowerCellPhone]) {
                    trigger(LoanInformationVariables.BorrowerCellPhone);
                  }
                }}
                value={watcher[LoanInformationVariables.BorrowerCellPhone]}
              />
            </div>
            <div className={styles.rowContainer}>
              <NumberInput
                label={LoanInformationVariables.BorrowerDateOfBirth}
                placeholder="MM/DD/YYYY"
                errorMessage={errors[LoanInformationVariables.BorrowerDateOfBirth]?.message}
                className={styles.formInput}
                name={LoanInformationVariables.BorrowerDateOfBirth}
                onChange={(event) => {
                  clearErrors(LoanInformationVariables.BorrowerDateOfBirth);
                  setValue(LoanInformationVariables.BorrowerDateOfBirth, event.target.value);
                }}
                onBlur={() => {
                  if (watcher[LoanInformationVariables.BorrowerDateOfBirth]) {
                    trigger(LoanInformationVariables.BorrowerDateOfBirth);
                  }
                }}
                format="##/##/####"
                value={watcher[LoanInformationVariables.BorrowerDateOfBirth]}
                mask="_"
                allowEmptyFormatting
              />
            </div>
            <div className={styles.consentText}>
              I agree to receive text messages from HFS Financial, LLC at the phone number provided. These messages may
              include transactional communications, status updates, and appointment reminders. Message frequency may
              vary. Standard message and data rates may apply. Reply STOP at any time to unsubscribe. For help, reply
              HELP or contact us at 800-254-9560. Please review our{' '}
              <a href="https://www.hfsfinancial.net/privacy-policy/" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>{' '}
              to learn how your data is used.
            </div>
            <Controller
              name={LoanInformationVariables.FullConsentCheckbox}
              control={control}
              rules={{
                required: false,
              }}
              render={() => (
                <div className={styles.checkboxContainer}>
                  <Checkbox
                    className={styles.checkboxText}
                    type={CheckboxType.Alternative}
                    name="agreementCheckbox"
                    isChecked={!!watcher[LoanInformationVariables.FullConsentCheckbox]}
                    onChange={() => {
                      setValue(
                        LoanInformationVariables.FullConsentCheckbox,
                        !watcher[LoanInformationVariables.FullConsentCheckbox],
                      );

                      if (isSubmitted) {
                        trigger();
                      }
                    }}
                    text={`You expressly consent to receiving calls and messages, including auto-dialed and pre-recorded
                     message calls, and SMS messages (including text messages) from HFS Financial at any telephone numbers
                      that you have provided or may provide in the future (including any cellular telephone numbers).  
                      HFS Financial may send text messages in various formats and with various contents, including 
                      but not limited to, text messages about products, services, and transactions you have requested/initiated; 
                      and/or marketing the products and services offered by or through us. You are the owner or authorized
                       user of the mobile phone number you have provided and you will notify us immediately 
                       if you are no longer the owner or authorized user of the mobile phone number you have provided.
                        Your cellular or mobile telephone provider will charge you according to the type of plan you carry.`}
                    labelClassName={styles.checkboxTextContainer}
                  />
                </div>
              )}
            />
          </FormBody>
          <div className={styles.nextButton}>
            <Button onClick={handleSubmitLoanForm} isLoading={isLoading}>
              I Agree
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default BorrowerIdentity;
